import { useEffect } from 'react';
import {
  Spacer,
  Input,
  Table,
  Headers,
  Button,
  SearchBarWithDropdown,
} from '@tw/tw-component-library';
import { InvoiceFormProps } from '../../types/invoice.types';
import { Container } from '../../styles/invoice.styles';
import { TABLE_COLUMNS } from '../../constants/invoice.constants';
import { transformInvoiceDataToTableData } from '../../utils/invoice.utils';
import { useInvoiceForm } from './useInvoiceForm';
import { MessageContainer } from './invoice.styles';

export const InvoiceForm: React.FC<InvoiceFormProps> = ({
  isLoadingMatters,
  isLoadingInvoices,
  clientData,
  matterData,
  invoiceData,
  error: parentError,
  onClientSelect,
  onClientSearch,
  onMatterSelect,
}) => {
  const {
    formState: {
      selectedClient,
      selectedMatter,
      billNumber,
      selectedBillNumber,
      showMatterDropdown,
      fileUpload,
      invoicesWithDocs,
      fileInputKey,
    },
    loadingState: { isSearching, isUploading, isDeleting, isLoadingDocs },
    messageState: {
      search: searchError,
      upload: uploadError,
      delete: deleteError,
      docs: docsError,
      success: success,
    },
    handlers: {
      handleBillNumberSearch,
      handleDocumentSelection,
      handleDeleteSelectedDocuments,
      handleClientSelect,
      handleClientSearch,
      handleMatterSelect,
      handleFileSelect,
      handleUploadClick,
      handleRowClick,
      setBillNumber,
    },
  } = useInvoiceForm(
    onClientSelect,
    onClientSearch,
    onMatterSelect,
    invoiceData
  );

  const renderMessage = (message: string | null, type: 'success' | 'error') =>
    message && (
      <>
        <MessageContainer type={type}>
          {type === 'success' ? '✓' : '⚠'} {message}
        </MessageContainer>
        <Spacer />
      </>
    );

  const renderError = (error: string | null) => renderMessage(error, 'error');

  // Show parent error if present
  useEffect(() => {
    if (parentError) {
      renderError(parentError);
    }
  }, [parentError]);

  const isTableLoading = isLoadingInvoices || isLoadingMatters || isLoadingDocs;

  return (
    <Container>
      <Headers headerType="h1">Request Details</Headers>
      <Spacer />

      {/* Client Section */}
      <section>
        <Headers headerType="label">Client</Headers>
        <SearchBarWithDropdown
          options={clientData}
          placeholder="Enter Client..."
          onSelect={handleClientSelect}
          onSearch={handleClientSearch}
          searchButton={false}
          value={selectedClient}
          isLoading={isLoadingMatters}
        />
        <Spacer />
      </section>

      {/* Matter Section */}
      {selectedClient &&
        (matterData?.length > 0 ? (
          <section>
            <Headers headerType="label">Matter</Headers>
            <SearchBarWithDropdown
              options={matterData}
              placeholder="Enter Matter..."
              onSelect={handleMatterSelect}
              onSearch={() => {}}
              searchButton={false}
              value={selectedMatter}
              isOpenOverride={showMatterDropdown && !!selectedClient}
              setIsOpenOverride={(isOpen) =>
                handleMatterSelect(isOpen ? selectedMatter : '')
              }
              isLoading={isLoadingMatters}
            />
            <Spacer />
          </section>
        ) : (
          renderMessage('No matters found for this client', 'error')
        ))}

      {/* Bill Number Search Section */}
      <section>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end' }}>
          <Input
            type="search"
            style={{ flex: 1 }}
            label="Bill Number"
            placeholder="Enter Bill Number..."
            value={billNumber}
            onChange={(e) => setBillNumber(e.target.value)}
            disabled={isSearching}
          />
          <Button
            onClick={handleBillNumberSearch}
            isLoading={isSearching}
            disabled={isSearching || !billNumber.trim()}
          >
            Search
          </Button>
        </div>
        {renderError(searchError)}
        <Spacer />

        {/* File Upload Section */}
        <section>
          {selectedBillNumber && (
            <div style={{ marginBottom: '16px' }}>
              <Headers headerType="label">
                Selected Bill Number: {selectedBillNumber}
              </Headers>
            </div>
          )}
          <Input
            key={fileInputKey}
            type="file"
            label="Upload Documents"
            accept=".pdf,.doc,.docx,.txt,.xlsx,.csv,.jpg,.jpeg,.png"
            multiple
            maxFileSize={10000000}
            onFileSelect={handleFileSelect}
            tooltip="Accepted formats: PDF, DOC, DOCX, TXT, XLSX, CSV, JPG, JPEG, PNG. Max size: 10MB"
            required
            disabled={!selectedBillNumber || isUploading}
          />
          <Spacer />
          {fileUpload.isValid && selectedBillNumber && (
            <>
              <Button
                onClick={handleUploadClick}
                disabled={isUploading || !fileUpload.files.length}
                isLoading={isUploading}
              >
                {isUploading ? 'Uploading...' : 'Upload Document'}
              </Button>
              <Spacer />
            </>
          )}
          {renderMessage(success, 'success')}
          {renderError(uploadError)}
        </section>
      </section>

      {/* Table Section */}
      <section>
        {invoicesWithDocs.some(
          (invoice) => invoice.selectedDocuments.length > 0
        ) && (
          <>
            <Button
              onClick={handleDeleteSelectedDocuments}
              disabled={isDeleting || isTableLoading}
              isLoading={isDeleting}
            >
              Delete Selected Documents
            </Button>
            <Spacer />
          </>
        )}

        {renderError(deleteError)}
        {renderError(docsError)}
        <Table
          data={transformInvoiceDataToTableData(
            invoicesWithDocs,
            handleDocumentSelection
          )}
          columns={TABLE_COLUMNS}
          enableSort={true}
          emptyStateMessage={
            isTableLoading
              ? 'Loading...'
              : searchError || parentError || 'No invoices found'
          }
          isLoading={isTableLoading}
          onRowClick={handleRowClick}
        />
      </section>
    </Container>
  );
};

export default InvoiceForm;
